import { Suspense, lazy, useCallback, useState } from 'react';
import type { ComponentType, JSX, LazyExoticComponent } from 'react';
import { CanvasBoundary, ScreenLayout, SkeletonBlock } from '@taki/ui';
import { useCadreSurface } from '@taki/ui/cadre-canvas';
import type { AppDeclaree, ProprietesDeSurface } from './app-declaree.js';
import { IconeDeFiche } from './icone.js';
import { useChatterDuGabarit } from './chatter.js';
import { useAppRuntime } from './runtime.js';
import { creerCacheDeSurfaces } from './surface-paresseuse.js';
/** App Canvas : le cadre et la surface sont deux responsabilites separees.
 * Le cadre lit la declaration unique et rend les composants natifs ; la surface
 * fournit leurs donnees et commandes par l'API commune aux portes et prototypes.
 */
const surfaceEnCache = creerCacheDeSurfaces<LazyExoticComponent<ComponentType<ProprietesDeSurface>>>();

function surfaceDe(app: AppDeclaree, essai: number): LazyExoticComponent<ComponentType<ProprietesDeSurface>> | null {
  const charger = app.appCanvas?.charger;
  if (charger === undefined) return null;
  return surfaceEnCache(app, essai, () => lazy(charger));
}

/** Ce que la zone 5 montre pendant que le morceau de la surface arrive. */
function AttenteDeSurface(): JSX.Element {
  return (
    <div className="flex h-full flex-col gap-3 p-6" aria-busy="true">
      <SkeletonBlock w="40%" h="1.25rem" />
      <SkeletonBlock h="100%" rounded="var(--radius-md)" className="min-h-[12rem] flex-1" />
    </div>
  );
}

export function EcranAppCanvas({ app }: { app: AppDeclaree }): JSX.Element {
  const { sdk } = useAppRuntime();
  const canvas = { level: 'app' as const, ...app.fiche.canvas?.app };
  const [recherche, setRecherche] = useState('');
  const { api, props } = useCadreSurface(app.id, canvas, recherche);
  const [ficheMontree, setFicheMontree] = useState<{ family: string; recordId: string; titre: string } | null>(null);
  const poserFiche = useCallback((suivante: { family: string; recordId: string; titre: string } | null) => {
    setFicheMontree((avant) => (avant?.recordId === suivante?.recordId && avant?.titre === suivante?.titre ? avant : suivante));
  }, []);
  const chatter = useChatterDuGabarit(sdk, ficheMontree !== null ? { ...ficheMontree, appId: app.id, doorId: app.id } : null);
  const [essai, setEssai] = useState(0);
  const reessayer = useCallback(() => setEssai((n) => n + 1), []);
  const Surface = surfaceDe(app, essai);

  return (
    <ScreenLayout
      canvas={canvas}
      appName={app.fiche.menu.label}
      appIcon={<IconeDeFiche nom={app.fiche.icon} size={18} />}
      title={app.fiche.name}
      searchValue={recherche}
      onSearchChange={setRecherche}
      chatter={chatter}
      {...props}
    >
      <CanvasBoundary surfaceId={app.id} onRetry={reessayer}>
        {Surface === null ? null : (
          <Suspense fallback={<AttenteDeSurface />}>
            <Surface appId={app.id} {...api} poserFiche={poserFiche} />
          </Suspense>
        )}
      </CanvasBoundary>
    </ScreenLayout>
  );
}

export default EcranAppCanvas;
